import { useState, useEffect } from 'react';
import { Link } from 'react-router';

import { LazyLoadImage } from 'react-lazy-load-image-component';

import { api, useAPIContext } from '../../../../api/context';
import dialogs from '../../../../dialogs';
import toast from '../../../../utils/toast';
import Activity from './Activity';
import ActivityGroup from './ActivityGroup';
import styles from './Campaign.module.scss';

const Campaign = ({ campaign }) => {

  const { token } = useAPIContext();

  /*
   * dialog
   */

  const { dialog } = dialogs.utils.useDialog();
  const [ dialogShow, setDialogShow ] = useState();

  useEffect(() => {

    const [ kind, text, ...rest ] = (dialog || '').split(' ');

    const value = (
      kind === 'campaign' &&
      text === (campaign.id + '') &&
      rest.length === 0
    );

    setDialogShow(value);

  }, [dialog, campaign]);

  /*
   * items
   */

  const [ itemsLoading, setItemsLoading ] = useState(false);
  const [ items, setItems ] = useState([]);

  useEffect(() => {

    if (campaign.user.active) {

      setItemsLoading(true);
      setItems([]);

      api(token).v1.campaigns.activities
        .query({ campaign: campaign.id })
        .then(res => {
          setItemsLoading(false);
          setItems(res.body.items);
        })
        .catch(err => {
          setItemsLoading(false);
          toast.show(toast.KIND.WARNING, err);
        });
    }

  }, [token, campaign, campaign.user.active]);

  const handle = (item, data) => {

    item.user.status = data.result.status;

    api(token).v1.campaigns.activities
      .query({ campaign: campaign.id })
      .then(res => {
        setItems(res.body.items);
      })
      .catch(err => {
        toast.show(toast.KIND.WARNING, err);
      });
  };

  /*
   * claim (e.g. missing)
   */

  const [ claimLoading, setClaimLoading ] = useState(false);

  const claim = (field, value) => {

    setClaimLoading(true);

    const data = {};
    data[field] = value;

    api(token).v1.campaigns.claim
      .post(
        { campaign: campaign.id },
        data,
      )
      .then(res => {
        setClaimLoading(false);

        if (res.status === 200) {
          campaign.user.claim[field].value = value;
        }

        if (res.status === 400) {
          toast.show(toast.KIND.ERROR, res.body);
        }
      })
      .catch(err => {
        setClaimLoading(false);
        toast.show(toast.KIND.WARNING, err);
      });
  };

  /*
   * content
   */

  return (
    <div className={styles.root}>
      {
        dialogShow
        ? <dialogs.campaigns.status.OptInOut campaign={campaign} />
        : null
      }

      <div className={styles.info}>
        <h2 id={campaign.slug}>
          {campaign.title.external}
        </h2>

        <div
          className={styles.content}
          dangerouslySetInnerHTML={{
            __html: (
              campaign.user.active
              ? campaign.content.member
              : campaign.content.public
            ),
          }}
        />

        <div className={styles.image}>
          <LazyLoadImage
            src={campaign.image}
            alt={campaign.title.external}
          />
        </div>
      </div>

      {
        campaign.user.active &&
        (campaign.user.claim.missing.show || campaign.user.tracking_url) && (

          <div className={styles.missing}>
            {
              campaign.user.claim.missing.show &&
              campaign.user.claim.missing.value && (
                <>
                  <div className={styles.text}>
                    Has your box arrived by now?
                  </div>

                  <div className={styles.action}>
                    <button
                      type='button'
                      disabled={claimLoading}
                      onClick={() => claim('missing', false)} >

                      I have received my box

                    </button>
                  </div>
                </>
              )
            }

            {
              campaign.user.claim.missing.show &&
              !campaign.user.claim.missing.value && (
                <>
                  <div className={styles.text}>
                    Has your box not arrived yet?
                  </div>

                  <div className={styles.action}>
                    <button
                      type='button'
                      disabled={claimLoading}
                      onClick={() => claim('missing', true)} >

                      I have not received my box

                    </button>
                  </div>
                </>
              )
            }

            {
              campaign.user.tracking_url && (
                <div className={styles.action}>
                  <a
                    href={campaign.user.tracking_url}
                    target='_blank'
                    rel='noopener noreferrer'>

                    Track package

                  </a>
                </div>
              )
            }
          </div>
        )
      }

      <div className={styles.items}>
        {
          itemsLoading
          ? 'Loading...'
          : ''
        }

        {
          items &&
          items.length > 0 &&
          items.map(item => (
            item.type === 'group'
            ? (
              <ActivityGroup
                key={`group-${item.id}`}
                campaign={campaign}
                group={item}
                handle={(child, data) => handle(child, data)}
              />
            )
            : (
              <Activity
                key={item.id}
                campaign={campaign}
                activity={item}
                handle={(data) => handle(item, data)}
              />
            )
          ))
        }
      </div>

      <div className={styles.actions}>
        {
          campaign.user.enable && (
            <Link
              to={`?dialog=campaign+${campaign.id}`}
              aria-label='opt in'>

              Opt In

            </Link>
          )
        }
      </div>
    </div>
  );
};

export default Campaign;